import React, { useState } from 'react';
import { useAppStore } from '@/store';
import { Coins, LogOut, Menu, X, Receipt } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BottomNavigation } from '@/components/navigation/BottomNavigation';
import { NotificationBell } from '@/components/NotificationBell';
import { getPrimaryNavItems, PROFILE_SECONDARY_LINKS, getActivePrimaryNav, getPageTitle, shouldHideBottomNav } from '@/config/navigation';

interface MobileLayoutProps {
  children: React.ReactNode;
}

const MOBILE_HEADER_HEIGHT = '5rem';
const MOBILE_NAV_HEIGHT = '5.5rem';

export const MobileLayout: React.FC<MobileLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, fastLogout, userBalance, fetchUserBalance, balanceLoading } = useAppStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [navKey, setNavKey] = useState(0); // Force re-render of navigation

  // Check if user has low tokens (<= 100)
  const lowTokens = userBalance ? userBalance.available_tokens <= 100 : false;
  const primaryNavItems = getPrimaryNavItems(lowTokens);
  const activePrimary = getActivePrimaryNav(location.pathname, primaryNavItems);
  const pageTitle = getPageTitle(location.pathname);
  const hideBottomNav = shouldHideBottomNav(location.pathname);

  // Staff-only admin links live in the "More" section without their own title.
  const secondaryLinks = React.useMemo(
    () =>
      user?.is_staff
        ? [...PROFILE_SECONDARY_LINKS, { label: 'Transactions', href: '/admin/transactions', icon: Receipt }]
        : PROFILE_SECONDARY_LINKS,
    [user?.is_staff],
  );

  // Debug logging
  console.log('MobileLayout - userBalance:', userBalance);
  console.log('MobileLayout - available_tokens:', userBalance?.available_tokens);
  console.log('MobileLayout - lowTokens:', lowTokens);
  console.log('MobileLayout - primaryNavItems:', primaryNavItems);
  console.log('MobileLayout - navKey:', navKey);

  // Force navigation re-render when userBalance changes
  React.useEffect(() => {
    if (userBalance) {
      setNavKey(prev => prev + 1);
    }
  }, [userBalance]);

  React.useEffect(() => {
    if (user && !userBalance) {
      fetchUserBalance().catch(console.error);
    }
  }, [user, userBalance]);

  React.useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousBodyHeight = document.body.style.height;
    const previousHtmlHeight = document.documentElement.style.height;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.height = '100%';
    document.documentElement.style.height = '100%';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.style.height = previousBodyHeight;
      document.documentElement.style.height = previousHtmlHeight;
    };
  }, []);

  // Close the drawer on Escape and whenever the route changes.
  React.useEffect(() => {
    if (!sidebarOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSidebarOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [sidebarOpen]);

  React.useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    fastLogout();
  };

  return (
    <div className="fixed inset-0 overflow-hidden bg-[#f8f4ec] text-slate-900 lg:hidden">
      <header className="absolute inset-x-0 top-0 z-40">
        <div className="mx-auto max-w-md border-b border-white/70 bg-[#fffaf2]/94 backdrop-blur-xl supports-[backdrop-filter]:bg-[#fffaf2]/88">
          <div className="flex items-center justify-between px-4 py-3.5">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="rounded-2xl p-2 text-gray-600 transition-colors hover:bg-white/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
                aria-label="Open navigation menu"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-[1.35rem] font-semibold tracking-[-0.01em] text-slate-900">{pageTitle.title}</h1>
                <p className="text-xs text-slate-500">{pageTitle.subtitle}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/billing')}
                className="flex items-center gap-1 rounded-full border border-orange-200/80 bg-white px-2.5 py-1 text-xs text-orange-700 shadow-sm transition-colors hover:bg-orange-50"
                aria-label="Open billing and tokens"
              >
                <Coins className="h-3 w-3" />
                <span className="font-medium">
                  {balanceLoading ? '...' : (userBalance?.available_tokens ?? 0) === 0 ? 'Add Token' : (userBalance?.available_tokens ?? 0)}
                </span>
              </button>
              <NotificationBell buttonClassName="relative flex h-10 w-10 items-center justify-center rounded-2xl border border-white/70 bg-white/80 text-slate-600 shadow-sm transition-colors hover:bg-white" />
            </div>
          </div>
        </div>
      </header>

      <div className={`absolute inset-0 z-50 ${sidebarOpen ? 'block' : 'hidden'}`}>
        <button
          className="absolute inset-0 bg-black/40"
          onClick={() => setSidebarOpen(false)}
          aria-label="Close navigation overlay"
        />
        <aside className="absolute left-0 top-0 flex h-full w-[82%] max-w-[320px] flex-col bg-[#fffdf9] shadow-2xl">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Navigation</h2>
              <p className="text-sm text-gray-500">Find your PT essentials</p>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="rounded-xl p-2 text-gray-600 hover:bg-gray-100"
              aria-label="Close navigation menu"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="border-b border-gray-200 px-4 py-4">
            <button
              onClick={() => {
                navigate('/profile');
                setSidebarOpen(false);
              }}
              className="flex w-full items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-3 py-3 text-left"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-orange-500 text-sm font-semibold text-white">
                {user?.first_name?.[0] || ''}{user?.last_name?.[0] || ''}
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">{user?.first_name} {user?.last_name}</p>
                <p className="text-xs text-gray-500">Student profile</p>
              </div>
            </button>
          </div>

          <nav aria-label="Primary" className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-4 [scrollbar-width:thin]" key={navKey}>
            <div className="space-y-1">
              {primaryNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = activePrimary === item.key;

                return (
                  <button
                    key={`${item.key}-${navKey}`}
                    onClick={() => {
                      navigate(item.href);
                      setSidebarOpen(false);
                    }}
                    className={`flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left text-sm font-medium transition-colors ${
                      isActive ? 'bg-orange-50 text-orange-700' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="mt-6 border-t border-gray-200 pt-4">
              <p className="px-3 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">More</p>
              {secondaryLinks.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href || location.pathname.startsWith(`${item.href}/`);
                const isExternal = (item as any).external;

                if (isExternal) {
                  return (
                    <a
                      key={item.href}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left text-sm font-medium text-gray-700 hover:bg-gray-100"
                    >
                      <Icon className="h-5 w-5" />
                      <span>{item.label}</span>
                    </a>
                  );
                }

                return (
                  <button
                    key={item.href}
                    onClick={() => {
                      navigate(item.href);
                      setSidebarOpen(false);
                    }}
                    className={`flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left text-sm font-medium transition-colors ${
                      isActive ? 'bg-orange-50 text-orange-700' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>
          </nav>

          <div className="border-t border-gray-200 p-3" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)' }}>
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
            >
              <LogOut className="h-5 w-5" />
              <span>Logout</span>
            </button>
          </div>
        </aside>
      </div>

      <main
        className="absolute inset-x-0 mx-auto max-w-md overflow-y-auto overflow-x-hidden"
        style={{
          top: MOBILE_HEADER_HEIGHT,
          bottom: hideBottomNav
            ? '0'
            : `calc(${MOBILE_NAV_HEIGHT} + env(safe-area-inset-bottom, 0px) + 0.5rem)`,
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <div className="min-h-full">
          {children}
        </div>
      </main>

      {!hideBottomNav && (
        <div className="absolute inset-x-0 bottom-0 z-40 mx-auto max-w-md">
          <BottomNavigation />
        </div>
      )}
    </div>
  );
};
